/* tslint:disable:member-ordering TODO: disable this rule*/
import { EgiPageConfig, EgiPageMetaData, EgiRoute, PageFeature } from './types';
import { FeatureType } from './feature-type';

// Afgeleiden worden één keer berekend uit de (readonly) config: dezelfde objectreferenties
// bij elke aanroep, zodat lijsten die erop renderen niet bij elke change detection
// hertekenen. De caches staan in WeakMaps en niet als property op de instantie, zodat ze ook
// op bevroren objecten werken (Akita bevroor store-state in dev-mode; Akita is sinds de
// Angular 21-upgrade weg, het patroon blijft omdat het de modellen vrij houdt van mutatie).
const asLinkedListCache = new WeakMap<EgiPage, EgiPage[]>();
const fullPathFromRootCache = new WeakMap<EgiPage, string>();
const regularFeaturesCache = new WeakMap<EgiPage, PageFeature[]>();
const clickThroughFeaturesCache = new WeakMap<EgiPage, PageFeature[]>();
const featuresCache = new WeakMap<EgiPage, PageFeature[]>();

export class EgiPage {
  constructor(
    public readonly path: string,
    public readonly parent: EgiRouteParent,
    public readonly config: EgiPageConfig | undefined
  ) {
  }

  get features(): PageFeature[] {
    let features = featuresCache.get(this);
    if (!features) {
      features = [...this.regularFeatures, ...this.clickThroughFeatures];
      featuresCache.set(this, features);
    }
    return features;
  }

  get regularFeatures(): PageFeature[] {
    let features = regularFeaturesCache.get(this);
    if (!features) {
      features = this.mapFeaturesDictionaryToPageFeatures();
      regularFeaturesCache.set(this, features);
    }
    return features;
  }

  private get clickThroughFeatures(): PageFeature[] {
    let features = clickThroughFeaturesCache.get(this);
    if (!features) {
      features = this.mapClickThroughPagesToPageFeatures();
      clickThroughFeaturesCache.set(this, features);
    }
    return features;
  }

  get metaData(): EgiPageMetaData {
    return {
      config: this.config,
      fullPathFromRoot: this.fullPathFromRoot,
      isPage: this.isPage,
      isRoot: this.isRoot,
      features: this.features
    };
  }

  public children: EgiPage[] = [];

  get id(): string {
    return this.config.id;
  }

  get topic(): string | undefined {
    return this.config.topic;
  }

  /**
   * Puts the current item with its children in an array.
   */
  get asLinkedList(): EgiPage[] {
    let linkedList = asLinkedListCache.get(this);
    if (!linkedList) {
      linkedList = [];
      this.flattenNestedChildren(this, linkedList);
      asLinkedListCache.set(this, linkedList);
    }

    return linkedList;
  }

  get isRoot(): boolean {
    return this.parent === 'root';
  }

  get isPage(): boolean {
    return !!this.config;
  }

  get fullPathFromRoot(): string {
    let fullPath = fullPathFromRootCache.get(this);
    if (fullPath === undefined) {
      fullPath = this.getPathFromRootRecursively();
      fullPathFromRootCache.set(this, fullPath);
    }
    return fullPath;
  }

  static createFrom(egiRoute: EgiRoute): EgiPage {
    return this.createRecursively(egiRoute, 'root');
  }

  private getPathFromRootRecursively(): string {
    let path = this.path;

    if (this.parent !== 'root') {
      const separator = this.path ? '/' : '';
      path = `${this.parent.fullPathFromRoot}${separator}${this.path}`;
    }

    return path;
  }

  private flattenNestedChildren(item: EgiPage, into: EgiPage[]): void {
    if (item.isPage) {
      into.push(item);
    }
    item.children.forEach(c => this.flattenNestedChildren(c, into));
  }

  mapFeaturesDictionaryToPageFeatures(): PageFeature[] {
    const { features } = this.config;
    const keys = Object.keys(features);
    return keys.map(name => ({
      featureId: features[name].id,
      name,
      pageId: this.config.id,
      type: FeatureType.Regular,
      tags: features[name].tags,
      requiredDeviceTypes: features[name].requiredDeviceTypes
    }));
  }

  private mapClickThroughPagesToPageFeatures(): PageFeature[] {
    const { clickTroughPages } = this.config;
    if (!clickTroughPages) {
      return [];
    }
    const keys = Object.keys(clickTroughPages);
    return keys.map(name => ({
      featureId: clickTroughPages[name].id,
      pageId: this.config.id,
      name,
      type: FeatureType.ClickThrough
    }));
  }

  private static createRecursively(egiRoute: EgiRoute, parent: EgiRouteParent): EgiPage {
    const config = egiRoute.data?.config;
    const { path } = egiRoute;
    const item = new EgiPage(path, parent, config);
    this.fillUpChildren(egiRoute, item);

    return item;
  }

  private static fillUpChildren(egiRoute: EgiRoute, item: EgiPage): void {
    const childrenRoutes = egiRoute.children || [];
    item.children = childrenRoutes.map(c => this.createRecursively(c, item));
  }
}

export type EgiRouteParent = EgiPage | 'root';
